/* eslint-disable max-classes-per-file */
(() => {
  // Sibling section requests keyed by URL, shared across form instances so a prefetch survives the swap
  const siblingSectionRequests = new Map();
  const SIBLING_SECTION_TIMEOUT = 8000;

  function _parseJson(string, fallback = {}) {
    try {
      return JSON.parse(string);
    } catch (_) {
      return fallback;
    }
  }
  class BundleProductForm extends HTMLElement {
    timeoutID = null;

    selectedVariants = null;

    allVariantsSelected = null;

    allVariantsAvailable = null;

    constructor() {
      super();
      this.form = this.querySelector('form') || this.closest('form');

      this.handle = this.dataset.bundleHandle;
      this.container = this.closest('[data-bundle-container]');
      this.isProductTemplate = this.container?.dataset.bundleContainer === 'product-template';

      this.commonOptionName = this.getAttribute('data-bundle-common-option-name');
      this.commonOptionWrapper = this.querySelector('[data-bundle-common-option]');
      this.commonOptionSelectedValue = this.querySelector('[data-bundle-common-option-selected-value]');

      this.customOptions = _parseJson(this.dataset.bundleCustomOptions, []);

      this.bundleProductItems = Array.from(this.querySelectorAll('bundle-product-item'));

      this.bundleSelectionInput = this.querySelector('input[data-bundle-selection]');

      const wrapper = this.closest('[data-quick-view-item]') || this;

      this.bundleSubmitButtons = wrapper.querySelectorAll('button[data-bundle-submit-btn]');

      this.errorContainer = wrapper.querySelector('[data-cart-errors-container]');
      this.errorMessage = this.errorContainer.querySelector('[data-error]');

      this.deliveryDates = [...document.querySelectorAll('[data-delivery-variant-id]')];

      this.bundlePreorderMessage = this.querySelector('[data-bundle-item-preorder]');
      this.bundlePreorderCountdown = this.querySelector('[data-bundle-item-preorder] .variant__countdown');
    }

    connectedCallback() {
      window.theme.selectedBundleOptions = window.theme.selectedBundleOptions || {};
      if (!this.isProductTemplate && this.closest('[data-scroll-lock-scrollable]')) {
        this.closest('[data-scroll-lock-scrollable]').scrollTop = window.theme.quickViewPopupScrollTop || 0;
      }

      if (this.isProductTemplate && this.container.hasAttribute('data-bundle-container-sibling')) {
        window.theme.reinitSection(this.container.dataset.bundleContainer, this.container);
      }

      if (!this.listenersInitialized) {
        this.listenersInitialized = true;
        this.setEventListeners();
      }
      this.sortDeliveryDates();
      this.setActiveDeliveryDate();

      window.queueMicrotask(() => {
        this.querySelectorAll('input.input-bundle-common-option:checked').forEach(
          this.handleCommonOptionChange.bind(this),
        );
      });
    }

    sortDeliveryDates() {
      this.deliveryDates.sort((first, second) => {
        const firstDeliveryDays = parseInt(first.dataset.deliveryDays);
        const secondDeliveryDays = parseInt(second.dataset.deliveryDays);
        return secondDeliveryDays - firstDeliveryDays;
      });
    }

    setActiveDeliveryDate() {
      if (!this.allVariantsSelected) {
        this.deliveryDates.forEach((item, index) => item.classList.toggle('hidden', index !== 0));
        return;
      }

      const selectedVariantIds = this.selectedVariants.map((v) => `${v.id}`);
      let isDisplayed = false;

      this.deliveryDates.forEach((item) => {
        if (!isDisplayed && selectedVariantIds.includes(item.dataset.deliveryVariantId)) {
          item.classList.remove('hidden');
          isDisplayed = true;
          return;
        }
        item.classList.add('hidden');
      });
    }

    setError(message = '') {
      if (this.errorMessage) {
        this.errorMessage.innerText = message;
      } else {
        this.errorContainer.innerHTML = message
          ? `<svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clip-path="url(#clip0_6615_37546)">
              <path d="M8 8.93777L8 4.33105M8 12.1982L8 11.9958L8 11.6722M1 8.33105C1 4.46506 4.13401 1.33105 8 1.33105C11.866 1.33105 15 4.46506 15 8.33105C15 12.197 11.866 15.3311 8 15.3311C4.13401 15.3311 1 12.197 1 8.33105Z" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
            </g>
            <defs>
              <clipPath id="clip0_6615_37546">
                <rect width="16" height="16" fill="transparent" transform="matrix(-1 -8.74228e-08 -8.74228e-08 1 16 0.331055)"/>
              </clipPath>
            </defs>
          </svg>
          <div class="errors text-inter" data-error autofocus>
            ${message}
          </div>`
          : '';
      }
      this.errorContainer.classList.toggle('is-visible', message);
      if (message) {
        this.errorContainer.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    }

    setEventListeners() {
      this.commonOptionWrapper?.addEventListener('change', (event) => this.handleCommonOptionChange(event.target));
      ['pointerover', 'touchstart', 'focusin'].forEach((type) =>
        this.commonOptionWrapper?.addEventListener(type, this.prefetchSibling.bind(this), { passive: true }),
      );
      this.bundleSubmitButtons.forEach((button) => button.addEventListener('click', this.handleAddToCart.bind(this)));
    }

    async handleAddToCart(event) {
      event.preventDefault();
      const clickedButton = event.currentTarget;

      // The selected sibling's form is still loading, so the current selection is outdated
      if (this.pendingSiblingHandle) return;

      if (!this.allVariantsSelected) {
        this.setError(window.theme.strings.option_not_selected_error);
        return;
      }

      this.bundleSubmitButtons.forEach((button) => button.classList.add('is-loading'));

      const formData = new FormData(this.form);

      const selectedCommonValue = this.commonOptionSelectedValue?.innerText;

      let propertiesData = '';

      this.selectedVariants.forEach((variant, index) => {
        const nameParts = variant.name.split(' - ');
        const key = variant.productTitle;
        let value = nameParts[1];

        if (this.customOptions?.length) {
          value = '';
          const selectedCustomOption = this.customOptions.find((option) => option.value === selectedCommonValue);
          const productSlectedOption = selectedCustomOption.productOptions[variant.key];

          const variantOptions = [
            { key: 'option1', position: 1 },
            { key: 'option2', position: 2 },
            { key: 'option3', position: 3 },
          ];
          variantOptions.forEach(({ key, position }) => {
            if (!variant[key] || position === productSlectedOption.position) return;
            value = value ? ` / ${variant[key]}` : variant[key];
          });
        } else if (selectedCommonValue) {
          value = value.replace(`${selectedCommonValue} /`, '').replace(`/ ${selectedCommonValue}`, '');
        }

        propertiesData += `${key}:${value}`;
        if (index < this.selectedVariants.length - 1) propertiesData += '/';
      });

      formData.append(`properties[_bundle_data]`, propertiesData);

      const etaDate = this.selectedVariants
        .map((variant) => variant?.eta_date)
        .filter((date) => date != null)
        .sort((a, b) => new Date(b) - new Date(a))[0];

      if (etaDate) {
        formData.append(`properties[_orderType]`, 'pre-order');
        formData.append(`properties[_statusEta]`, etaDate);
      }

      try {
        const response = await fetch(theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/javascript',
          },
          body: formData,
        });

        const responseJson = await response.json();

        if (!response.ok) {
          const errorMessage =
            responseJson.message === responseJson.description
              ? responseJson.description
              : `${responseJson.message}: ${responseJson.description}`;

          this.setError(errorMessage);
          this.bundleSubmitButtons.forEach((button) => button.classList.remove('is-loading'));
          return;
        }
        await this.updateCartAttributes(responseJson);
        const shouldShowCartConfirmation = this.isProductTemplate
          && !this.closest('.popup-quick-view')
          && window.innerWidth >= window.theme.sizes.small
          && window.theme.cartConfirmationPopup?.open(responseJson, clickedButton);

        if (window.cart) {
          window.cart.suppressNextCartDrawerOpen = Boolean(shouldShowCartConfirmation);
        }
        window.cart.getCart();

        document.dispatchEvent(
          new CustomEvent('theme:cart:added', {
            bubbles: true,
            detail: {
              selector: clickedButton,
              item: responseJson,
            },
          }),
        );
      } catch (error) {
        this.setError(error.message);
      }
      this.bundleSubmitButtons.forEach((button) => button.classList.remove('is-loading'));
    }

    async updateCartAttributes(item) {
      const newPreorderDate = item.properties?.['_statusEta'];
      if (!newPreorderDate) return;

      const currentPreorderDate = window.theme.statusEta;
      let attributes = {};

      // if the new preorder date is later than the current one, update it in cart attributes
      if (currentPreorderDate && (new Date(currentPreorderDate) > new Date(newPreorderDate))) return;

      window.theme.statusEta = newPreorderDate;
      attributes = {
        'status_eta': newPreorderDate,
      };

      return await fetch(theme.routes.cart_update_url + '.js', {
        method: 'POST',
        headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify({
          attributes
        }),
      });
    }

    handleCommonOptionChange(input) {
      if (input instanceof HTMLInputElement && this.commonOptionSelectedValue) {
        // Any newer selection, including the current product, cancels a pending sibling swap
        this.pendingSiblingHandle = null;
        const siblingHandle = input.dataset.bundleSiblingHandle;
        if (siblingHandle !== this.handle && siblingHandle) return this.handleSiblingChange(siblingHandle);

        const { value } = input;
        this.commonOptionSelectedValue.innerText = value;

        this.bundleProductItems.forEach((item) => {
          if (!(item instanceof BundleProductItem)) return;
          if (!this.customOptions?.length) return item.setCommonOptionValue(value);

          const customOption = this.customOptions.find((option) => option.value === value);
          const itemKey = item.productJson.key;

          const optionValue = customOption.productOptions[itemKey].value;
          item.setCommonOptionValue(optionValue);
          const activeVariant = item.productJson.variants.find((el) => el.options.includes(optionValue));
          item.setActiveVariantID(activeVariant.id);

          // Unset selection if the selected variant is not available and not NOS
          if (item && !item?.selectedVariant?.nos && !item?.available) {
            item.setActiveVariantID(null);
          }
        });
      }
    }

    getSiblingSectionUrl(siblingHandle) {
      return `${window.Shopify.routes.root}products/${siblingHandle}?section_id=${this.container.dataset.sectionId}`;
    }

    // The product page doesn't pre-render sibling layouts, so they're fetched with the Section Rendering API
    fetchSiblingSection(siblingHandle) {
      const url = this.getSiblingSectionUrl(siblingHandle);
      if (!siblingSectionRequests.has(url)) {
        // Abort a stalled request so the sibling swap falls back to navigating instead of blocking add to cart
        const controller = new AbortController();
        const timeoutID = setTimeout(() => controller.abort(), SIBLING_SECTION_TIMEOUT);
        const request = fetch(url, { signal: controller.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`Sibling section request failed with ${response.status}`);
            return response.text();
          })
          .finally(() => clearTimeout(timeoutID));
        request.catch(() => siblingSectionRequests.delete(url));
        siblingSectionRequests.set(url, request);
      }
      return siblingSectionRequests.get(url);
    }

    prefetchSibling(event) {
      if (!this.isProductTemplate) return;
      const label = event.target.closest?.('label');
      const input = label?.control || event.target.closest?.('input[data-bundle-sibling-handle]');
      const siblingHandle = input?.dataset.bundleSiblingHandle;
      if (siblingHandle && siblingHandle !== this.handle) this.fetchSiblingSection(siblingHandle).catch(() => {});
    }

    // Returns the pre-rendered <template> (quick view) or the fetched product container (product page)
    async getSiblingLayout(siblingHandle) {
      // Only quick view uses templates, an open quick view's templates must not replace the product page form
      if (!this.isProductTemplate) {
        return document.querySelector(`template[data-bundle-sibling-layout="${siblingHandle}"]`);
      }

      const html = await this.fetchSiblingSection(siblingHandle);
      const newContainer = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('[data-bundle-container="product-template"]');
      if (!newContainer) return null;

      // Match what the sibling layouts used to render with `is_sibling: true`
      newContainer.setAttribute('data-bundle-container-sibling', '');
      newContainer.querySelectorAll('[data-aos]').forEach((element) => element.removeAttribute('data-aos'));
      const siblingInput = newContainer.querySelector(
        `input.input-bundle-common-option[data-bundle-sibling-handle="${siblingHandle}"]`,
      );
      siblingInput?.setAttribute('checked', '');

      return newContainer;
    }

    async handleSiblingChange(siblingHandle) {
      if (!this.isProductTemplate) {
        window.theme.quickViewPopupScrollTop = this.closest('[data-scroll-lock-scrollable]')?.scrollTop || 0;
      }
      this.pendingSiblingHandle = siblingHandle;

      let newLayout = null;
      try {
        newLayout = await this.getSiblingLayout(siblingHandle);
      } catch (_) {
        newLayout = null;
      }

      // Ignore the result if another sibling was picked meanwhile or the form was replaced
      if (this.pendingSiblingHandle !== siblingHandle || !this.isConnected) return;

      if (!newLayout) {
        window.location.href = `${window.Shopify.routes.root}products/${siblingHandle}`;
        return;
      }

      const isTemplate = newLayout instanceof HTMLTemplateElement;
      const layoutRoot = isTemplate ? newLayout.content : newLayout;
      layoutRoot.querySelectorAll('button[data-bundle-submit-btn]').forEach((button) => {
        button.className = this.bundleSubmitButtons[0].className;
        button.disabled = this.bundleSubmitButtons[0].disabled;
      });

      this.container.outerHTML = isTemplate ? newLayout.innerHTML : newLayout.outerHTML;

      if (this.isProductTemplate) {
        const newPath = `${window.Shopify.routes.root}products/${siblingHandle}`;
        window.history.replaceState({}, '', newPath);
      } else {
        window.theme.quickViewDrawer?.reload();
      }
    }

    onBundleItemChange() {
      clearTimeout(this.timeoutID);
      this.timeoutID = setTimeout(this.handleBundleSelection.bind(this), 10);
    }

    handleBundleSelection() {
      this.setError();
      this.selectedVariants = this.bundleProductItems.map((item) => item.selectedVariant);

      this.allVariantsSelected = this.selectedVariants.every((variant) => variant);
      this.allVariantsAvailable = this.selectedVariants.every((variant) => variant?.available);

      // Check if any selected variant is not available and not NOS
      this.nonNosNotAvailable =
        this.selectedVariants.length > 0 &&
        this.selectedVariants.some((variant) => !variant?.nos && !variant?.available);

      if (this.nonNosNotAvailable) {
        this.allVariantsSelected = false;
      }

      const etaDate = this.selectedVariants
        .map((variant) => variant?.eta_date)
        .filter((date) => date != null)
        .sort((a, b) => new Date(b) - new Date(a))[0];

      if (etaDate) {
        this.bundlePreorderMessage.classList.remove('hidden');
        this.bundlePreorderCountdown.classList.add('count-is-preorder');
        this.bundlePreorderCountdown.querySelector('[data-preorder-date]').innerText = Intl.DateTimeFormat(
          `${window.Shopify.locale}-${window.Shopify.country}`,
          {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
          },
        ).format(new Date(etaDate));
      } else {
        this.bundlePreorderMessage.classList.add('hidden');
        this.bundlePreorderCountdown.classList.remove('count-is-preorder');
      }

      if (!this.allVariantsSelected) {
        this.bundleSubmitButtons.forEach((button) => {
          button.disabled = false;
          button.classList.remove('bundle-products__submit-btn--add');
          button.classList.remove('bundle-products__submit-btn--sold-out');
          button.classList.add('bundle-products__submit-btn--select-size');
        });
      } else if (!this.allVariantsAvailable) {
        this.bundleSubmitButtons.forEach((button) => {
          button.disabled = true;
          button.classList.remove('bundle-products__submit-btn--add');
          button.classList.remove('bundle-products__submit-btn--select-size');
          button.classList.add('bundle-products__submit-btn--sold-out');
        });
        this.bundlePreorderMessage.classList.add('hidden');
        this.bundlePreorderCountdown.classList.remove('count-is-preorder');
      } else {
        this.bundleSubmitButtons.forEach((button) => {
          button.disabled = false;
          button.classList.add('bundle-products__submit-btn--add');
          button.classList.remove('bundle-products__submit-btn--sold-out');
          button.classList.remove('bundle-products__submit-btn--select-size');
        });
        this.bundleSelectionInput.value = this.selectedVariants.map((variant) => variant.id).join(',');
      }

      this.setActiveDeliveryDate();
    }
  }
  if (!customElements.get('bundle-product-form')) customElements.define('bundle-product-form', BundleProductForm);

  class BundleProductItem extends HTMLElement {
    selectedVariant = null;

    filteredVariants = [];

    constructor() {
      super();
      this.commonOptionBadge = this.querySelector('[data-bundle-common-option-selected-value-badge]');
      this.commonOptionInputs = [...this.querySelectorAll('[data-bundle-common-option-input]')];
      this.productJson = _parseJson(this.querySelector('[data-bundle-product-json]').innerHTML);

      // Merge NOS information into the variants
      this.productJson.variants = this.productJson.variants.map((variant) => ({
        ...variant,
        nos: this.productJson.nos_variant_ids?.filter((id) => id !== '')?.includes(variant.id?.toString()),
      }));

      this.productJson.key = this.dataset.bundleProductItemKey;
      this.bundleImage = this.querySelector('img[data-bundle-item-image]');
      this.bundleProductForm = this.closest('bundle-product-form');

      this.bundleSoldOutMessage = this.querySelector('[data-bundle-item-sold-out]');
      this.bundleSoldOutCountdown = this.querySelector('[data-bundle-item-sold-out] .variant__countdown');
      this.bundleNotifyButton = this.querySelector('[data-bundle-item-notify-button]');

      this.dropdownToggles = [...this.querySelectorAll('[data-bundle-dropdown-toggle]')];
    }

    connectedCallback() {
      if (!this.listenersInitialized) {
        this.listenersInitialized = true;
        this.setEventListeners();
      }
      this.setPersistedOptions();
      this.querySelectorAll('input:checked').forEach(this.handleOptionChange.bind(this));
      this.onVariantChange();
    }

    setPersistedOptions() {
      const selectedOptions = window.theme.selectedBundleOptions[this.productJson.key];
      if (!selectedOptions) return;

      Object.entries(selectedOptions).forEach(([name, value]) => {
        const input = this.querySelector(`input[name="${this.productJson.key}--${name}"][value="${value}"]`);
        if (!input) return;
        input.checked = true;

        const label = input.nextElementSibling;
        if (!label) return;
        label.style.transition = 'none';
        window.queueMicrotask(() => (label.style.transition = ''));
      });
    }

    setEventListeners() {
      window.addEventListener('load', this.setSelectedVariant.bind(this));
      this.addEventListener('change', (event) => this.handleOptionChange(event.target));

      this.bundleNotifyButton?.addEventListener('click', () => {
        new window.NotificationPopup(this.querySelector('[data-notification-popup]'));
      });

      this.dropdownToggles.forEach((toggle) => {
        const details = toggle.closest('details[data-bundle-dropdown]');
        if (!details) return;
        details.addEventListener('toggle', () => {
          this.updateDropdownLabelForToggle(toggle, details.open);
        });
      });

      this.handleDocumentClick = (event) => {
        this.querySelectorAll('details[data-bundle-dropdown][open]').forEach((details) => {
          if (!details.contains(event.target)) {
            details.removeAttribute('open');
          }
        });
      };
      document.addEventListener('click', this.handleDocumentClick);
    }

    disconnectedCallback() {
      if (this.handleDocumentClick) {
        document.removeEventListener('click', this.handleDocumentClick);
      }
    }

    handleOptionChange(input) {
      const optionName = input.name.replace(`${this.productJson.key}--`, '');
      const optionValue = input.value;

      const isCommonOption = this.commonOptionInputs.includes(input);
      if (!isCommonOption) {
        const currentValues = window.theme.selectedBundleOptions[this.productJson.key] || {};
        window.theme.selectedBundleOptions[this.productJson.key] = { ...currentValues, [optionName]: optionValue };
      }

      const optionPosition = `option${input.dataset.optionPosition}`;

      const nextOptionName = this.productJson.options.find((option) => option !== optionName);
      const nextOptionPosition = optionPosition === 'option1' ? 'option2' : 'option1';

      const filteredVariants = this.productJson.variants.filter((variant) => variant[optionPosition] === optionValue);
      const filteredAvailableVariants = filteredVariants.filter((variant) => variant.available);
      const filteredUnavailableVariants = filteredVariants.filter((variant) => !variant.available);

      const availableNextOptionValues = filteredAvailableVariants.map((variant) => variant[nextOptionPosition]);
      const unavailableNextOptionValues = filteredUnavailableVariants.map((variant) => variant[nextOptionPosition]);

      const setInputState = (input) => {
        const isSoldOut = !availableNextOptionValues.includes(input.value);
        const isUnavailable = !unavailableNextOptionValues.includes(input.value) && isSoldOut;

        const inputWrapper = input.parentElement;

        inputWrapper.classList.toggle('unavailable', isUnavailable);
        inputWrapper.classList.toggle('sold-out', isSoldOut && !isUnavailable);

        if (isUnavailable && input.checked) {
          input.checked = false;
          this.querySelector(`input[name="${input.name}"]:not(span.unavailable input[name="${input.name}"])`).checked =
            true;
        }
      };

      this.querySelectorAll(`input[name="${this.productJson.key}--${nextOptionName}"]`).forEach(setInputState);

      this.setSelectedVariant();
      this.updateDropdownLabels();
      this.closeDropdown(optionName);
    }

    setSelectedVariant() {
      const selectedOptions = {};
      this.querySelectorAll('input:checked').forEach((input) => {
        const optionPosition = `option${input.dataset.optionPosition}`;
        selectedOptions[optionPosition] = input.value;
      });

      this.filteredVariants = this.productJson.variants.filter((variant) =>
        Object.keys(selectedOptions).every((key) => variant[key] === selectedOptions[key]),
      );
      const allOptionsSelected = Object.keys(selectedOptions).length === this.productJson.options.length;

      this.selectedVariant =
        this.filteredVariants.length === 1 && allOptionsSelected
          ? { ...this.filteredVariants[0], key: this.productJson.key, productTitle: this.productJson.title }
          : null;

      this.onVariantChange();
      this.updateDropdownLabels();
      this.bundleProductForm.onBundleItemChange();
    }

    onVariantChange() {
      if (this.bundleImage) {
        if (this.selectedVariant?.featured_image) {
          this.bundleImage.src = `${this.selectedVariant.featured_image.src}&width=250`;
        } else {
          const image = this.filteredVariants[0]?.featured_image?.src || this.productJson.featured_image;
          this.bundleImage.src = `${image}&width=250`;
        }
      }

      if (!this.selectedVariant) return;

      this.bundleSoldOutMessage.classList.toggle('hidden', this.selectedVariant.available);
      this.bundleSoldOutCountdown.classList.toggle('count-is-out', !this.selectedVariant.available);

      // Hide the sold out message if the variant is not available and not NOS
      if (!this.selectedVariant.available && !this.selectedVariant.nos) {
        this.bundleSoldOutMessage.classList.add('hidden');
      }

      this.selectedVariant.is_preorder = false;
      this.selectedVariant.eta_date = null;

      this.productJson.preorder_variant_ids.forEach((id, index) => {
        if (id.toString() === this.selectedVariant.id.toString()) {
          this.selectedVariant.is_preorder = true;
          this.selectedVariant.eta_date = this.productJson.status_eta[index];
        }
      });

      this.updateNotifyButton();
    }

    updateNotifyButton() {
      if (this.selectedVariant.available || !this.bundleNotifyButton) return;

      const notificationPopupHtml = this.bundleNotifyButton.getAttribute('data-notification-popup');
      const notificationPopupDOM = new DOMParser().parseFromString(notificationPopupHtml, 'text/html');

      const variantTitle = notificationPopupDOM.querySelector('[data-bundle-notify-variant-title]');
      const variantID = notificationPopupDOM.querySelector('[data-notification-form]');

      if (variantTitle) {
        variantTitle.innerText = this.selectedVariant.title;
      }

      if (variantID) {
        variantID.setAttribute('data-notification-form', this.selectedVariant.id);
      }

      this.bundleNotifyButton.setAttribute(
        'data-notification-popup',
        notificationPopupDOM.querySelector('[data-notification]').outerHTML,
      );
    }

    setCommonOptionValue(value) {
      if (this.commonOptionBadge) {
        const colorValue = this.commonOptionBadge.querySelector('.bundle-product-card__color-value');
        if (colorValue) {
          colorValue.textContent = value;
        } else {
          this.commonOptionBadge.textContent = value;
        }
      }

      this.commonOptionInputs.forEach((input) => {
        const isChecked = input.value === value;
        input.checked = isChecked;
        if (isChecked) this.handleOptionChange(input);
      });
    }

    setActiveVariantID(variantId) {
      const quickViewBtn = this.querySelectorAll('[data-button-quick-view]');
      if (!quickViewBtn.length) return;
      quickViewBtn.forEach((el) => {
        el.setAttribute('data-variant-id', variantId);
      });
    }

    updateDropdownLabels() {
      if (!this.dropdownToggles.length) return;

      this.dropdownToggles.forEach((toggle) => this.updateDropdownLabelForToggle(toggle, false));
    }

    updateDropdownLabelForToggle(toggle, isOpen) {
      if (!toggle) return;

      const option = toggle.dataset.bundleOptionName;
      const openLabel = toggle.dataset.openLabel || option;
      const label = toggle.querySelector('[data-bundle-dropdown-label]');

      if (!label || !option || !openLabel) return;

      if (isOpen) {
        label.textContent = `${openLabel}:`;
      } else {
        const selector = `input[name="${this.productJson.key}--${option}"]:checked`;
        const checked = this.querySelector(selector);

        if (checked) {
          label.textContent = `${openLabel}: ${checked.value}`;
          return;
        }

        label.textContent = toggle.dataset.defaultLabel || openLabel;
      }
    }

    closeDropdown(optionName) {
      if (!this.dropdownToggles.length) return;

      const toggle = this.dropdownToggles.find((item) => item.dataset.bundleOptionName === optionName);
      if (!toggle) return;

      const details = toggle.closest('details[data-bundle-dropdown]');
      if (!details) return;

      details.removeAttribute('open');
    }
  }
  if (!customElements.get('bundle-product-item')) customElements.define('bundle-product-item', BundleProductItem);
})();
